import React, { forwardRef, RefObject, useRef } from 'react';
import classNames from 'classnames';

import { Comp, useTheme } from '@redsift/design-system';

import { AxisProps, AxisPosition, AxisVariant } from './types';
import { StyledAxis } from './styles';
import { animated, useSpring, useTransition } from '@react-spring/web';
import { computeTicks } from './computeTicks';
import { config } from '../../config';
import { AnyScale } from '../../types';

const COMPONENT_NAME = 'Axis';
const CLASSNAME = 'redsift-axis';

export const getAxisType = (position: AxisPosition) =>
  [AxisPosition.top, AxisPosition.bottom].includes(position) ? 'x' : 'y';

export const Axis: Comp<AxisProps, SVGGElement> = forwardRef((props, ref) => {
  const {
    className,
    disableAnimations,
    grid,
    length,
    legend,
    legendOffset = -32,
    legendPosition = 'end',
    legendX: propsLegendX,
    legendY: propsLegendY,
    legendRotation: propsLegendRotation,
    legendStyle,
    position = AxisPosition.bottom,
    scale,
    tickPadding = 5,
    tickRotation = 0,
    tickSize = 5,
    tickValues,
    tickFormat,
    tickRemodelling,
    tickStyle,
    variant = AxisVariant.default,
    x = 0,
    y = 0,
    chartWidth,
    chartHeight,
    sectionHeight,
    textColor,
    tickToColor = () => true,
    ...forwardedProps
  } = props;
  const theme = useTheme();
  const axis = getAxisType(position!);
  const { length: gridLength, ...gridStyle } =
    typeof grid === 'object'
      ? { length: axis === 'x' ? sectionHeight ?? chartHeight : chartWidth, ...grid }
      : grid === true
      ? {
          length: axis === 'x' ? sectionHeight ?? chartHeight : chartWidth,
        }
      : { length: 0 };

  const axisRef = (ref || useRef<SVGGElement>()) as RefObject<SVGGElement>;

  const animatedProps = useSpring({
    ...config,
    to: async (next: (props?: object) => Promise<void>) => {
      await next({
        lineX2: axis === 'x' ? length : 0,
        lineY2: axis === 'x' ? 0 : length,
      });
    },
    from: { lineX2: 0, lineY2: 0 },
    reset: false,
  });

  let legendNode = null;
  if (legend !== undefined) {
    let legendX = 0;
    let legendY = 0;
    let legendRotation = 0;
    let textAnchor;

    if (axis === 'y') {
      legendRotation = propsLegendRotation ?? 90;
      legendX = propsLegendX ?? legendOffset!;
      if (legendPosition === 'start') {
        textAnchor = 'start';
        legendY = propsLegendY ?? length!;
      } else if (legendPosition === 'middle') {
        textAnchor = 'middle';
        legendY = propsLegendY ?? length! / 2;
      } else if (legendPosition === 'end') {
        textAnchor = 'end';
        legendY = propsLegendY ?? legendY;
      }
    } else {
      legendY = propsLegendY ?? legendOffset!;
      if (legendPosition === 'start') {
        textAnchor = 'start';
        legendX = propsLegendX ?? legendX;
      } else if (legendPosition === 'middle') {
        textAnchor = 'middle';
        legendX = propsLegendX ?? length! / 2;
      } else if (legendPosition === 'end') {
        textAnchor = 'end';
        legendX = propsLegendX ?? length!;
      }
    }

    legendNode = (
      <>
        <text
          transform={`translate(${legendX}, ${legendY}) rotate(${legendRotation})`}
          textAnchor={textAnchor}
          style={{
            dominantBaseline: 'central',
            ...legendStyle,
          }}
        >
          {legend}
        </text>
      </>
    );
  }

  const { ticks, textAlign, textBaseline } = computeTicks({
    axis,
    scale: scale as AnyScale,
    ticksPosition: [AxisPosition.top, AxisPosition.left].includes(position!) ? 'before' : 'after',
    tickValues,
    tickSize: tickSize!,
    tickPadding: tickPadding!,
    tickRotation: tickRotation!,
    tickRemodelling,
  });

  const transition = useTransition<(typeof ticks)[0], { opacity: number; transform: string; textTransform: string }>(
    ticks,
    {
      ...config,
      keys: (tick) => tick.key,
      initial: (tick) => ({
        opacity: 1,
        transform: `translate(${tick.x},${axis === 'x' ? tick.y : (y || 0) - tick.y}),rotate(${
          axis === 'x' ? 0 : 180
        })`,
        textTransform: `translate(${tick.textX},${tick.textY}),rotate(${tickRotation})`,
      }),
      from: (tick) => ({
        opacity: 0,
        transform: `translate(${tick.x},${axis === 'x' ? tick.y : (y || 0) - tick.y}),rotate(${
          axis === 'x' ? 0 : 180
        })`,
        textTransform: `translate(${tick.textX},${tick.textY}),rotate(${tickRotation})`,
      }),
      enter: (tick) => ({
        opacity: 1,
        transform: `translate(${tick.x},${axis === 'x' ? tick.y : (y || 0) - tick.y}),rotate(${
          axis === 'x' ? 0 : 180
        })`,
        textTransform: `translate(${tick.textX},${tick.textY}),rotate(${tickRotation})`,
      }),
      update: (tick) => ({
        opacity: 1,
        transform: `translate(${tick.x},${axis === 'x' ? tick.y : (y || 0) - tick.y}),rotate(${
          axis === 'x' ? 0 : 180
        })`,
        textTransform: `translate(${tick.textX},${tick.textY}),rotate(${tickRotation})`,
      }),
      leave: {
        opacity: 0,
      },
    }
  );

  const tickStyles = tickStyle ? tickStyle(ticks) : [];

  return (
    <StyledAxis
      aria-hidden="true"
      className={classNames(Axis.className, className)}
      $theme={theme}
      {...forwardedProps}
      transform={`translate(${x},${y})${axis === 'x' ? '' : ',rotate(180)'}`}
      ref={axisRef}
      $color={textColor}
    >
      {variant === AxisVariant.default ||
      variant === AxisVariant.tick ||
      variant === AxisVariant.lineTick ||
      variant === AxisVariant.tickValue ||
      grid
        ? transition((transitionProps, tick, _state, tickIndex) => {
            let formattedTickValues = tickFormat ? tickFormat(tick.value as any) : tick.value;
            formattedTickValues = Array.isArray(formattedTickValues) ? formattedTickValues : [formattedTickValues];

            return (
              <animated.g
                className={`${Axis.className}__tick`}
                key={tickIndex}
                transform={transitionProps.transform}
                style={{ opacity: transitionProps.opacity }}
              >
                {gridLength ? (
                  <line
                    className={`${Axis.className}-grid__line`}
                    x1={axis === 'x' ? tick.lineX : 0}
                    x2={axis === 'x' ? tick.lineX : gridLength! * (position === 'right' ? -1 : 1)}
                    y1={axis === 'x' ? 0 : tick.lineY}
                    y2={axis === 'x' ? gridLength! * (position === 'bottom' ? -1 : 1) : tick.lineY}
                    style={{
                      strokeDasharray: '4,4',
                      opacity: 0.2,
                      ...gridStyle,
                      strokeWidth:
                        tickStyles && tickStyles.length > 0
                          ? tickStyles[tickIndex]?.gridStrokeWidth || 0.5
                          : gridStyle.strokeWidth || 0.5,
                      stroke:
                        tickStyles && tickStyles.length > 0
                          ? tickStyles[tickIndex]?.gridStroke || 'currentColor'
                          : gridStyle.stroke || 'currentColor',
                    }}
                  />
                ) : null}
                <line
                  className={`${Axis.className}-tick__line`}
                  x1={0}
                  x2={tick.lineX}
                  y1={0}
                  y2={tick.lineY}
                  style={{
                    strokeWidth: tickStyles && tickStyles.length > 0 ? tickStyles[tickIndex]?.strokeWidth : 1,
                  }}
                />
                {variant === AxisVariant.default || variant === AxisVariant.tickValue
                  ? (formattedTickValues as string[]).map((value, index) => (
                      <animated.text
                        key={value}
                        dominantBaseline={textBaseline}
                        textAnchor={textAlign}
                        transform={`translate(${tick.textX},${tick.textY + 14 * index}),rotate(${tickRotation})`}
                        className={!textColor ? '' : tickToColor(tick, tickIndex) ? 'colored' : ''}
                      >
                        {value}
                      </animated.text>
                    ))
                  : null}
              </animated.g>
            );
          })
        : null}

      {variant === AxisVariant.default || variant === AxisVariant.line || variant === AxisVariant.lineTick ? (
        <animated.line
          className={`${Axis.className}__line`}
          x1={0}
          x2={config.immediate || disableAnimations ? (axis === 'x' ? length : 0) : animatedProps.lineX2}
          y1={0}
          y2={config.immediate || disableAnimations ? (axis === 'x' ? 0 : length) : animatedProps.lineY2}
        />
      ) : null}
      {legendNode}
    </StyledAxis>
  );
});
Axis.className = CLASSNAME;
Axis.displayName = COMPONENT_NAME;
